import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Mailbox, type MessageHeader, type EmailAccount } from '../lib/api';
import { formatRelative } from '../lib/format';

function mailboxIcon(specialUse: string | null, name: string): string {
  if (specialUse === '\\Inbox') return '📥';
  if (specialUse === '\\Sent') return '📤';
  if (specialUse === '\\Drafts') return '📝';
  if (specialUse === '\\Trash') return '🗑️';
  if (specialUse === '\\Junk') return '🚫';
  if (specialUse === '\\Archive') return '📦';
  const lower = name.toLowerCase();
  if (lower.includes('inbox') || lower.includes('eingang')) return '📥';
  if (lower.includes('sent') || lower.includes('gesendet')) return '📤';
  if (lower.includes('draft') || lower.includes('entwurf')) return '📝';
  if (lower.includes('trash') || lower.includes('papier') || lower.includes('gelöscht')) return '🗑️';
  if (lower.includes('junk') || lower.includes('spam')) return '🚫';
  if (lower.includes('archiv')) return '📦';
  return '📁';
}

function findInbox(mailboxes: Mailbox[]): Mailbox | undefined {
  return (
    mailboxes.find((m) => m.specialUse === '\\Inbox') ??
    mailboxes.find((m) => m.path.toUpperCase() === 'INBOX') ??
    mailboxes[0]
  );
}

export function InboxPage() {
  const { accountId, mailbox: encodedMailbox } = useParams<{
    accountId: string;
    mailbox?: string;
  }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSigEditor, setShowSigEditor] = useState(false);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: accountData } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ accounts: EmailAccount[] }>('/accounts'),
  });
  const account = accountData?.accounts.find((a) => a.id === accountId);

  const { data: mailboxData, error: mbError, isLoading: mbLoading } = useQuery({
    queryKey: ['mailboxes', accountId],
    queryFn: () => api.get<{ mailboxes: Mailbox[] }>(`/accounts/${accountId}/mailboxes`),
    enabled: !!accountId,
    staleTime: 60_000,
  });

  if (!encodedMailbox && mailboxData && mailboxData.mailboxes.length > 0) {
    const inbox = findInbox(mailboxData.mailboxes);
    if (inbox) {
      nav(`/account/${accountId}/inbox/${encodeURIComponent(inbox.path)}`, { replace: true });
      return null;
    }
  }

  const mailboxPath = encodedMailbox ? decodeURIComponent(encodedMailbox) : '';

  const { data: messagesData, isLoading: msgLoading } = useQuery({
    queryKey: ['messages', accountId, mailboxPath, page],
    queryFn: () =>
      api.get<{ total: number; messages: MessageHeader[]; page: number; pageSize: number }>(
        `/accounts/${accountId}/mailboxes/${encodeURIComponent(mailboxPath)}/messages?page=${page}`,
      ),
    enabled: !!accountId && !!mailboxPath && searchQuery.length === 0,
  });

  const searchResult = useQuery({
    queryKey: ['search', accountId, mailboxPath, searchQuery],
    queryFn: () =>
      api.get<{ total: number; messages: MessageHeader[] }>(
        `/accounts/${accountId}/mailboxes/${encodeURIComponent(mailboxPath)}/search?q=${encodeURIComponent(searchQuery)}`,
      ),
    enabled: !!accountId && !!mailboxPath && searchQuery.length > 0,
  });

  const messagesToShow = searchQuery ? searchResult.data?.messages ?? [] : messagesData?.messages ?? [];
  const isLoading = searchQuery ? searchResult.isLoading : msgLoading;
  const totalForPagination = messagesData?.total ?? 0;
  const pageSize = messagesData?.pageSize ?? 50;
  const totalPages = searchQuery ? 1 : Math.max(1, Math.ceil(totalForPagination / pageSize));

  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
      {/* Sidebar */}
      <aside className="space-y-1">
        <Link to="/" className="text-xs text-rla-700 hover:underline block mb-2">
          ← Konten
        </Link>
        <div className="text-xs text-slate-500 truncate mb-1" title={account?.emailAddress}>
          {account?.emailAddress}
        </div>
        {accountId && (
          <Link
            to={`/account/${accountId}/compose`}
            className="btn-primary block text-center text-sm mb-3"
          >
            ✏ Schreiben
          </Link>
        )}
        {mbLoading && <div className="text-sm text-slate-500">Lade Ordner …</div>}
        {mbError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2">
            Fehler: {(mbError as Error).message}
          </div>
        )}
        {mailboxData?.mailboxes.map((mb) => {
          const active = mb.path === mailboxPath;
          return (
            <Link
              key={mb.path}
              to={`/account/${accountId}/inbox/${encodeURIComponent(mb.path)}`}
              className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm ${
                active
                  ? 'bg-rla-100 text-rla-700 font-medium'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
              onClick={() => {
                setPage(1);
                setSearchInput('');
              }}
            >
              <span className="flex-shrink-0">{mailboxIcon(mb.specialUse, mb.name)}</span>
              <span className="truncate flex-1">{mb.name}</span>
              {mb.unseen > 0 && (
                <span className="text-xs bg-rla-500 text-white rounded-full px-1.5 py-0.5">
                  {mb.unseen}
                </span>
              )}
            </Link>
          );
        })}

        {/* Signatur-Button */}
        <button
          onClick={() => setShowSigEditor(true)}
          className="mt-3 w-full text-xs text-slate-500 hover:text-rla-700 hover:bg-slate-100 px-2 py-1.5 rounded text-left"
        >
          ✍ Signatur bearbeiten
        </button>

        {/* Signatur-Dialog */}
        {showSigEditor && account && (
          <SignatureEditor
            accountId={account.id}
            current={account.signature}
            onClose={() => setShowSigEditor(false)}
            onSaved={() => {
              setShowSigEditor(false);
              qc.invalidateQueries({ queryKey: ['accounts'] });
            }}
          />
        )}
      </aside>

      {/* Hauptbereich */}
      <section>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <span>
              {mailboxData
                ? mailboxIcon(
                    mailboxData.mailboxes.find((m) => m.path === mailboxPath)?.specialUse ?? null,
                    mailboxData.mailboxes.find((m) => m.path === mailboxPath)?.name ?? '',
                  )
                : '📥'}
            </span>
            {mailboxData?.mailboxes.find((m) => m.path === mailboxPath)?.name ?? mailboxPath}
          </h1>
          <input
            type="search"
            placeholder="In diesem Ordner suchen ..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="input flex-1 min-w-[200px] max-w-md ml-auto text-sm"
          />
        </div>

        {searchQuery && (
          <div className="text-sm text-slate-500 mb-2">
            🔍 Suche nach „{searchQuery}" — {searchResult.data?.total ?? 0} Treffer
          </div>
        )}

        {isLoading && <div className="text-slate-500">Lade Nachrichten …</div>}

        {!isLoading && messagesToShow.length === 0 && (
          <div className="card text-center text-slate-500">
            {searchQuery ? `Keine Treffer für „${searchQuery}"` : 'Keine Nachrichten.'}
          </div>
        )}

        {messagesToShow.length > 0 && (
          <>
            <div className="card overflow-hidden p-0">
              <ul className="divide-y divide-slate-100">
                {messagesToShow.map((m) => {
                  const unread = !m.flags.includes('\\Seen');
                  return (
                    <li key={m.uid}>
                      <Link
                        to={`/account/${accountId}/message/${encodeURIComponent(mailboxPath)}/${m.uid}`}
                        className="flex items-start gap-3 px-3 py-2 hover:bg-slate-50"
                      >
                        <div className="w-2 h-2 mt-2 rounded-full flex-shrink-0">
                          {unread && <div className="w-2 h-2 rounded-full bg-rla-500" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <span
                              className={`text-sm truncate ${unread ? 'font-bold' : 'font-medium'} text-slate-800`}
                            >
                              {m.fromName || m.fromAddress}
                            </span>
                            <span className="text-xs text-slate-400 ml-auto flex-shrink-0">
                              {formatRelative(m.date)}
                            </span>
                          </div>
                          <div className={`text-sm truncate ${unread ? 'text-slate-900' : 'text-slate-600'}`}>
                            {m.subject}
                          </div>
                          {m.hasAttachments && (
                            <div className="text-xs text-slate-400">📎 Anhang</div>
                          )}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {!searchQuery && totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 text-sm">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary text-sm"
                >
                  ← Neuer
                </button>
                <span className="text-slate-500">
                  Seite {page} / {totalPages} ({totalForPagination} Mails)
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="btn-secondary text-sm"
                >
                  Älter →
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Signatur-Editor Dialog                                              */
/* ------------------------------------------------------------------ */

function SignatureEditor({
  accountId,
  current,
  onClose,
  onSaved,
}: {
  accountId: string;
  current: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [sig, setSig] = useState(current ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/accounts/${accountId}/signature`, { signature: sig });
      onSaved();
    } catch {
      setError('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-slate-900">E-Mail-Signatur</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500">
            ✕
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-500">
            Diese Signatur wird automatisch unter jede ausgehende Mail gesetzt.
          </p>
          <textarea
            className="input min-h-[150px] font-sans text-sm"
            value={sig}
            onChange={(e) => setSig(e.target.value)}
            placeholder={"Mit freundlichen Grüßen,\nIhr Name\nPosition / Firma"}
          />
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <div className="font-medium mb-1">Vorschau:</div>
            <div className="whitespace-pre-line border-t border-slate-200 pt-2 text-slate-700">
              {sig || 'Keine Signatur gesetzt'}
            </div>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="btn-secondary">Abbrechen</button>
            <button onClick={save} disabled={saving} className="btn-primary">
              {saving ? 'Speichere …' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
